import { forwardRef } from "react";
import {
  bool,
  element,
  elementType,
  func,
  node,
  oneOfType,
  string,
} from "prop-types";
import styled from "styled-components/macro";
import { hideVisually, rem } from "polished";

import { colors } from "../Styles.variables";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookmark as faBookmarkSolid,
  faDownload,
  faEnvelopeSquare,
  faFileMedicalAlt,
  faFilePdf,
  faInfoCircle,
  faLink,
  faPhoneSquare,
  faShare,
  faShareAltSquare,
  faStar as faStarSolid,
  faSyncAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  faBookmark,
  faImages,
  faStar,
} from "@fortawesome/free-regular-svg-icons";
import {
  faFacebookSquare,
  faInstagram,
  faLinkedin,
  faTwitterSquare,
} from "@fortawesome/free-brands-svg-icons";

const getIcon = icon => {
  switch (icon) {
    case "activity":
      return faFileMedicalAlt;
    case "bookmark":
      return faBookmark;
    case "bookmarkSolid":
      return faBookmarkSolid;
    case "download":
      return faDownload;
    case "email":
      return faEnvelopeSquare;
    case "facebook":
      return faFacebookSquare;
    case "info":
      return faInfoCircle;
    case "instagram":
      return faInstagram;
    case "link":
      return faLink;
    case "linkedIn":
      return faLinkedin;
    case "loading":
      return faSyncAlt;
    case "pdf":
      return faFilePdf;
    case "phone":
      return faPhoneSquare;
    case "photos":
      return faImages;
    case "share":
      return faShare;
    case "shareAlt":
      return faShareAltSquare;
    case "starSolid":
      return faStarSolid;
    case "star":
      return faStar;
    case "twitter":
      return faTwitterSquare;
    default:
      return null;
  }
};

const ButtonIcon = forwardRef(
  (
    { children, icon, iconColor, spin, srOnly, tag: Component, ...rest },
    ref
  ) => {
    const lookup = getIcon(icon);

    return !!lookup && !!children ? (
      <Button {...rest} as={Component} ref={ref}>
        <Icon
          fixedWidth
          icon={lookup}
          iconColor={iconColor}
          spin={spin}
          srOnly={srOnly}
        />
        {srOnly ? (
          <SrOnly>
            <>{children}</>
          </SrOnly>
        ) : (
          <span>{children}</span>
        )}
      </Button>
    ) : null;
  }
);

const Button = styled.button`
  appearance: none;
  background: none;
  border: 0 none;
  color: inherit !important;
  line-height: 1;
  margin: 0;
  padding: 0;
  text-decoration: none;
  text-transform: ${({ lower }) => (lower ? "none" : "uppercase")};
  user-select: none;
  white-space: nowrap;

  &:hover > span {
    text-decoration: underline;
  }

  &:focus {
    outline: ${rem("1px")} dotted;
    outline-offset: 0.25em;
  }
`;

const Icon = styled(({ iconColor, srOnly, ...rest }) => (
  <FontAwesomeIcon {...rest} />
))`
  color: ${({ iconColor }) =>
    !!colors[iconColor] ? colors[iconColor] : "inherit"};
  margin-right: ${({ srOnly }) => (srOnly ? "0" : "0.28571429em")};
`;

const SrOnly = styled.span`
  ${hideVisually()}
`;

ButtonIcon.propTypes = {
  children: node.isRequired,
  icon: string.isRequired,
  iconColor: string,
  lower: bool,
  spin: bool,
  srOnly: bool,
  tag: oneOfType([element, elementType, node, func, string]),
};

ButtonIcon.defaultProps = {
  iconColor: "black",
  tag: "button",
};

export default ButtonIcon;
